import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { EventDialogComponent } from '../event-dialog/event-dialog.component';
import {
  CalendarDayEventsDialogResult,
  CalendarDayEventsDialogComponent,
} from './calendar-day-events-dialog.component';
import {
  Calendar,
  CalendarContainer,
  CalendarDay,
  CalendarEvent,
  CalendarEventSegment,
  CalendarTypeOption,
  RightEntity,
  RightAndGroup,
  RightState,
} from '../../services/definitions.service';
import { DataModelService } from '../../services/data-model.service';
import { AuthService } from '../../services/auth.service';
import { UserRightsManagementService } from '../../services/user-rights-management.service';
import { EventEmitterService } from '../../event-emitter.service';
import { CommonFunctionsService } from '../../services/common-functions.service';


@Component({
  selector: 'app-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.css'],
})
export class CalendarComponent implements OnInit, OnDestroy {
  maxVisibleEventRows: number = 2;
  private readonly baseMonth: Date = this.startOfMonth(new Date());
  private readonly calendarColorsStorageKey: string = 'calendar_colors_map';
  private readonly monthFormatter = new Intl.DateTimeFormat('de-DE', {
    month: 'long',
  });

  daysOfWeek: string[] = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  currentDate: Date = this.startOfMonth(new Date());
  today: Date = this.startOfDay(new Date());
  selectedDate: Date | null = null;
  selectedEvent: CalendarEvent | null = null;

  events: CalendarEvent[] = [];
  allEvents: CalendarEvent[] = [];
  calendarContainers: CalendarContainer[] = [];
  calendarTypeOptions: CalendarTypeOption[] = [];
  showCalendarSidebar: boolean = false;
  selectedCalendarIds: { [id: string]: boolean } = {};
  calendarColors: { [id: string]: string } = {};

  weeks: CalendarDay[][] = [];
  weekEventRows: CalendarEventSegment[][][] = [];
  visibleWeekEventRows: CalendarEventSegment[][][] = [];
  weekOverflowDotTypes: string[][][] = [];
  isCalendarDataLoading: boolean = false;
  canCreateCalendarEvents: boolean = false;
  canEditCalendarEvents: boolean = false;
  canDeleteCalendarEvents: boolean = false;
  canManageCalendarParticipants: boolean = false;
  canViewEventStatistics: boolean = false;
  private calendarAccessRightIds: Set<number> = new Set();
  private calendarAccessRightIdsForQuery: number[] = [];
  private calendarRightsLoaded: boolean = false;

  private calendarSubscription: Subscription;

  constructor(
    public dialog: MatDialog,
    private dataModel: DataModelService,
    private commonFunctions: CommonFunctionsService,
    private authService: AuthService,
    private userRightsManagement: UserRightsManagementService,
    private eventEmitter: EventEmitterService
  ) {}

  /**
   * Initializes persisted state and starts calendar subscriptions.
   */
  ngOnInit(): void {
    this.loadCalendarColorsFromStorage();
    this.buildCalendar();
    this.subscribeToCalendarData();
  }

  /**
   * Cleans up active subscriptions when component is destroyed.
   */
  ngOnDestroy(): void {
    this.calendarSubscription?.unsubscribe();
  }

  /**
   * Navigates to the previous month if within allowed range.
   */
  prevMonth(): void {
    if (!this.canGoPrev()) {
      return;
    }

    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth() - 1,
      1
    );
    this.selectedDate = null;
    this.buildCalendar();
  }

  /**
   * Navigates to the next month if within allowed range.
   */
  nextMonth(): void {
    if (!this.canGoNext()) {
      return;
    }

    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth() + 1,
      1
    );
    this.selectedDate = null;
    this.buildCalendar();
  }

  /**
   * Jumps to the current month and selects today.
   */
  goToToday(): void {
    this.currentDate = this.startOfMonth(new Date());
    this.selectedDate = this.startOfDay(new Date());
    this.buildCalendar();
  }

  /**
   * Selects a date and opens the day events dialog.
   */
  selectDate(date: Date): void {
    this.selectedDate = this.startOfDay(date);

    if (
      date.getMonth() !== this.currentDate.getMonth() ||
      date.getFullYear() !== this.currentDate.getFullYear()
    ) {
      if (!this.isMonthWithinRange(date)) {
        return;
      }

      this.currentDate = this.startOfMonth(date);
      this.buildCalendar();
    }

    this.openDayEventsDialog(this.selectedDate);
  }

  /**
   * Opens the event dialog for creating or editing an event.
   */
  openEventDialog(day: Date, event: CalendarEvent | null = null): void {
    const isEdit = !!event;
    const isReadOnly = isEdit ? !this.canEditCalendarEvents : !this.canCreateCalendarEvents;

    if (!isEdit && !this.canCreateCalendarEvents) {
      return;
    }

    const dialogRef = this.dialog.open(EventDialogComponent, {
      width: '420px',
      maxWidth: '95vw',
      data: {
        date: day,
        event,
        isReadOnly,
        eventTypes: this.calendarTypeOptions,
        selectedCalendarId: event ? this.getCalendarIdFromEvent(event) : undefined,
      },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) {
        return;
      }

      if (isReadOnly) {
        return;
      }

      const selectedCalendar = this.findCalendarById(result.calendarId);
      if (!selectedCalendar) {
        return;
      }

      const mappedEvent = this.dataModel.serializeCalendarEventFromUi({
        name: result.name,
        start: new Date(result.start),
        end: new Date(result.end),
        type: result.type,
        location: result.location,
      });

      if (mappedEvent.end.toDate() < mappedEvent.start.toDate()) {
        mappedEvent.end = mappedEvent.start;
      }

      if (event?.path) {
        mappedEvent.created = event.created;
        const updateResult = await this.dataModel.updateCalendarEvent(event.path, mappedEvent);
        if (!updateResult.result) {
          this.commonFunctions.showErrorToast(
            updateResult.errorMessage || 'Event konnte nicht gespeichert werden.'
          );
        }
      } else if (selectedCalendar.path) {
        const addResult = await this.dataModel.addCalendarEvent(selectedCalendar.path, mappedEvent);
        if (!addResult.result) {
          this.commonFunctions.showErrorToast(
            addResult.errorMessage || 'Event konnte nicht erstellt werden.'
          );
        }
      }
    });
  }

  /**
   * Opens edit mode for a clicked event segment.
   */
  editEvent(event: CalendarEvent, mouseEvent: MouseEvent): void {
    mouseEvent.stopPropagation();
    this.selectedEvent = event;
    const day = this.selectedDate || this.today;
    this.openEventDialog(day, event);
  }

  /**
   * Returns all events that overlap the given calendar date.
   */
  getEventsForDate(date: Date): CalendarEvent[] {
    const target = this.startOfDay(date);
    return this.events.filter((event) => {
      const start = this.startOfDay(this.asDate(event.start));
      const end = this.startOfDay(this.asDate(event.end));
      return target >= start && target <= end;
    });
  }

  /**
   * Checks whether a date is today.
   */
  isToday(date: Date): boolean {
    return this.startOfDay(date).getTime() === this.today.getTime();
  }

  /**
   * Checks whether a date is currently selected.
   */
  isSelected(date: Date): boolean {
    if (!this.selectedDate) {
      return false;
    }

    return this.startOfDay(date).getTime() === this.selectedDate.getTime();
  }

  /**
   * Resolves the display color for a calendar identifier.
   */
  getDotColor(type: string): string {
    const selectedCalendar = this.findCalendarById(type);
    if (selectedCalendar) {
      return this.getCalendarColor(selectedCalendar.id);
    }

    switch (type) {
      case 'meeting':
        return '#1a73e8';
      case 'task':
        return '#34a853';
      case 'reminder':
        return '#fbbc04';
      default:
        return '#5f6368';
    }
  }

  /**
   * Returns overflow indicator types for a specific week/day cell.
   */
  getOverflowDotTypes(weekIndex: number, dayIndex: number): string[] {
    if (
      !this.weekOverflowDotTypes[weekIndex] ||
      !this.weekOverflowDotTypes[weekIndex][dayIndex]
    ) {
      return [];
    }

    return this.weekOverflowDotTypes[weekIndex][dayIndex];
  }

  /**
   * Returns the localized label of the currently displayed month.
   */
  getCurrentMonthLabel(): string {
    return this.monthFormatter.format(this.currentDate);
  }

  /**
   * Indicates whether navigating to the previous month is allowed.
   */
  canGoPrev(): boolean {
    const minAllowed = this.addMonths(this.baseMonth, -6);
    return this.startOfMonth(this.currentDate) > minAllowed;
  }

  /**
   * Indicates whether navigating to the next month is allowed.
   */
  canGoNext(): boolean {
    const maxAllowed = this.addMonths(this.baseMonth, 6);
    return this.startOfMonth(this.currentDate) < maxAllowed;
  }

  /**
   * Opens the dialog showing events for a selected day.
   */
  openDayEventsDialog(date: Date): void {
    const userData = this.eventEmitter.cachedUserData;
    const dialogRef = this.dialog.open(CalendarDayEventsDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      height: '60vh',
      autoFocus: false,
      panelClass: 'calendar-events-dialog-panel',
      data: {
        date,
        events: this.getEventsForDate(date),
        canCreate: this.canCreateCalendarEvents,
        canEdit: this.canEditCalendarEvents,
        canDelete: this.canDeleteCalendarEvents,
        canManageParticipants: this.canManageCalendarParticipants,
        canViewStatistics: this.canViewEventStatistics,
        userId: this.authService.userId,
        userFirstName: userData?.personalData?.firstName || '',
        userLastName: userData?.personalData?.lastName || '',
      },
    });

    dialogRef.afterClosed().subscribe((result: CalendarDayEventsDialogResult) => {
      if (!result || !result.action) {
        return;
      }

      if (result.action === 'create') {
        if (!this.canCreateCalendarEvents) {
          return;
        }
        this.openEventDialog(date);
        return;
      }

      if (!result.event) {
        return;
      }

      if (result.action === 'edit') {
        if (!this.canEditCalendarEvents) {
          return;
        }
        this.openEventDialog(date, result.event);
        return;
      }

      if (result.action === 'delete') {
        if (!this.canDeleteCalendarEvents) {
          return;
        }
        this.deleteEvent(result.event);
      }
    });
  }

  /**
   * Toggles visibility of the calendar filter sidebar.
   */
  toggleCalendarSidebar(): void {
    this.showCalendarSidebar = !this.showCalendarSidebar;
  }

  /**
   * Closes the calendar filter sidebar.
   */
  closeCalendarSidebar(): void {
    this.showCalendarSidebar = false;
  }

  /**
   * Updates visibility state for a single calendar filter.
   */
  onCalendarVisibilityChange(calendarId: string, selected: boolean): void {
    this.selectedCalendarIds[calendarId] = selected;
    this.applySelectedCalendarFilter();
  }

  /**
   * Persists and applies a user-selected color for a calendar.
   */
  onCalendarColorInput(calendarId: string, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (!calendarId || !target?.value) {
      return;
    }

    this.calendarColors[calendarId] = target.value;
    this.saveCalendarColorsToStorage();
    this.buildCalendar();
  }

  /**
   * Returns whether a calendar is currently selected for display.
   */
  isCalendarSelected(calendarId: string): boolean {
    return this.selectedCalendarIds[calendarId] !== false;
  }

  /**
   * Subscribes to calendars and events and refreshes local view state.
   */
  private async subscribeToCalendarData(): Promise<void> {
    this.isCalendarDataLoading = true;
    await this.ensureCalendarRightsLoaded();

    this.calendarSubscription = this.dataModel
      .observeCalendarsWithEventsByRightIds(this.calendarAccessRightIdsForQuery)
      .subscribe((containers) => {
        this.calendarContainers = containers;
        this.calendarTypeOptions = containers.map((entry) => ({
          value: entry.calendar.id,
          label: entry.calendar.name,
        }));
        const incomingCalendarIds = new Set<string>();
        containers.forEach((entry) => {
          if (!entry.calendar?.id) {
            return;
          }

          incomingCalendarIds.add(entry.calendar.id);
          if (this.selectedCalendarIds[entry.calendar.id] === undefined) {
            this.selectedCalendarIds[entry.calendar.id] = true;
          }
        });

        Object.keys(this.selectedCalendarIds).forEach((existingId) => {
          if (!incomingCalendarIds.has(existingId)) {
            delete this.selectedCalendarIds[existingId];
          }
        });

        this.allEvents = containers.reduce(
          (allEvents, container) => allEvents.concat(container.events),
          [] as CalendarEvent[]
        );
        this.applySelectedCalendarFilter();
        this.isCalendarDataLoading = false;
      }, () => {
        this.isCalendarDataLoading = false;
        this.commonFunctions.showErrorToast(
          'Kalenderdaten konnten nicht geladen werden.'
        );
      });
  }

  /**
  * Loads effective rights and caches all allowed right ids for calendar access.
   */
  private async ensureCalendarRightsLoaded(): Promise<void> {
    if (this.calendarRightsLoaded) {
      return;
    }

    const userId = this.authService.userId;
    if (!userId) {
      return;
    }

    const loaded = await this.userRightsManagement.testing_getEffectiveUserRights(
      userId,
      [],
      true,
      false
    );

    if (!loaded.result || !Array.isArray(loaded.data)) {
      return;
    }

    const effectiveRights = loaded.data as RightAndGroup[];
    this.calendarAccessRightIdsForQuery = Array.from(
      new Set(
        effectiveRights
          .filter(
            (entry) =>
              Number.isFinite(Number(entry?.right?.id)) &&
              entry.right.state === RightState.ALLOW.valueOf()
          )
          .map((entry) => Number(entry.right.id))
      )
    );

    this.calendarAccessRightIds = new Set(this.calendarAccessRightIdsForQuery);
    this.canCreateCalendarEvents = this.calendarAccessRightIds.has(
      RightEntity.CALENDAR_CREATE_EVENTS.valueOf()
    );
    this.canEditCalendarEvents = this.calendarAccessRightIds.has(
      RightEntity.CALENDAR_EDIT_EVENTS.valueOf()
    );
    this.canDeleteCalendarEvents = this.calendarAccessRightIds.has(
      RightEntity.CALENDAR_DELETE_EVENTS.valueOf()
    );
    this.canManageCalendarParticipants = this.calendarAccessRightIds.has(
      RightEntity.CALENDAR_MANAGE_PARTICIPANTS.valueOf()
    );
    this.canViewEventStatistics = this.calendarAccessRightIds.has(
      RightEntity.CALENDAR_EVENT_STATISTICS.valueOf()
    );

    this.calendarRightsLoaded = true;
  }

  /**
   * Checks whether the current user has an allowed right matching calendar.right_id.
   */
  private hasAccessToCalendar(calendar: Calendar): boolean {
    const rightId = Number(calendar?.right_id);
    if (!Number.isFinite(rightId) || rightId <= 0) {
      return false;
    }

    return this.calendarAccessRightIds.has(rightId);
  }

  /**
   * Applies active calendar filters to the full event collection.
   */
  private applySelectedCalendarFilter(): void {
    this.events = this.allEvents.filter((event) =>
      this.isCalendarSelected(this.getCalendarIdFromEvent(event))
    );
    this.buildCalendar();
  }

  /**
   * Returns the effective color of a calendar, including custom overrides.
   */
  getCalendarColor(calendarId: string): string {
    if (!calendarId) {
      return '#5f6368';
    }

    return this.calendarColors[calendarId] ?? this.getColorByCalendarId(calendarId);
  }

  /**
   * Loads persisted calendar colors from local storage.
   */
  private loadCalendarColorsFromStorage(): void {
    const raw = localStorage.getItem(this.calendarColorsStorageKey);
    if (!raw) {
      this.calendarColors = {};
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      this.calendarColors = parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      this.calendarColors = {};
    }
  }

  /**
   * Saves current calendar color overrides to local storage.
   */
  private saveCalendarColorsToStorage(): void {
    localStorage.setItem(
      this.calendarColorsStorageKey,
      JSON.stringify(this.calendarColors)
    );
  }

  /**
   * Builds month grid, event rows and overflow indicators.
   */
  private buildCalendar(): void {
    const firstOfMonth = this.startOfMonth(this.currentDate);
    const startDayOffset = (firstOfMonth.getDay() + 6) % 7;
    const firstVisible = new Date(firstOfMonth);
    firstVisible.setDate(firstVisible.getDate() - startDayOffset);

    const days: CalendarDay[] = [];
    for (let index = 0; index < 42; index++) {
      const date = new Date(firstVisible);
      date.setDate(firstVisible.getDate() + index);

      days.push({
        date,
        currentMonth:
          date.getMonth() === this.currentDate.getMonth() &&
          date.getFullYear() === this.currentDate.getFullYear(),
      });
    }

    this.weeks = [];
    for (let index = 0; index < days.length; index += 7) {
      this.weeks.push(days.slice(index, index + 7));
    }

    this.weekEventRows = this.weeks.map((week) =>
      this.buildWeekEventRows(week[0].date, week[6].date)
    );

    this.visibleWeekEventRows = this.weekEventRows.map((rows) =>
      rows.slice(0, this.maxVisibleEventRows)
    );

    this.weekOverflowDotTypes = this.weekEventRows.map((rows) => {
      const hiddenRows = rows.slice(this.maxVisibleEventRows);
      const dotTypesPerDay: string[][] = Array.from({ length: 7 }, () => []);

      hiddenRows.forEach((row) => {
        row.forEach((segment) => {
          for (
            let dayColumn = segment.startCol;
            dayColumn <= segment.endCol;
            dayColumn++
          ) {
            dotTypesPerDay[dayColumn - 1].push(this.getCalendarIdFromEvent(segment.event));
          }
        });
      });

      return dotTypesPerDay;
    });
  }

  /**
   * Deletes a calendar event by its document path.
   */
  private deleteEvent(event: CalendarEvent): void {
    if (!this.canDeleteCalendarEvents) {
      return;
    }

    if (event.path) {
      this.dataModel.deleteCalendarEvent(event.path);
    }
  }

  /**
   * Builds collision-free event rows for a week.
   */
  private buildWeekEventRows(
    weekStartDate: Date,
    weekEndDate: Date
  ): CalendarEventSegment[][] {
    const weekStart = this.startOfDay(weekStartDate);
    const weekEnd = this.startOfDay(weekEndDate);

    const overlappingEvents = this.events
      .filter((event) => {
        const start = this.startOfDay(this.asDate(event.start));
        const end = this.startOfDay(this.asDate(event.end));
        return end >= weekStart && start <= weekEnd;
      })
      .sort((left, right) => {
        const startDiff =
          this.startOfDay(this.asDate(left.start)).getTime() -
          this.startOfDay(this.asDate(right.start)).getTime();

        if (startDiff !== 0) {
          return startDiff;
        }

        const leftDuration =
          this.startOfDay(this.asDate(left.end)).getTime() -
          this.startOfDay(this.asDate(left.start)).getTime();
        const rightDuration =
          this.startOfDay(this.asDate(right.end)).getTime() -
          this.startOfDay(this.asDate(right.start)).getTime();

        return rightDuration - leftDuration;
      });

    const rows: CalendarEventSegment[][] = [];

    overlappingEvents.forEach((event) => {
      const start = this.maxDate(
        this.startOfDay(this.asDate(event.start)),
        weekStart
      );
      const end = this.minDate(this.startOfDay(this.asDate(event.end)), weekEnd);
      const startCol = this.diffInDays(weekStart, start) + 1;
      const endCol = this.diffInDays(weekStart, end) + 1;

      const segment: CalendarEventSegment = { event, startCol, endCol };

      let placed = false;
      for (const row of rows) {
        const hasCollision = row.some(
          (item) =>
            !(segment.endCol < item.startCol || segment.startCol > item.endCol)
        );

        if (!hasCollision) {
          row.push(segment);
          placed = true;
          break;
        }
      }

      if (!placed) {
        rows.push([segment]);
      }
    });

    return rows;
  }

  /**
   * Returns the first day of the month for a date.
   */
  private startOfMonth(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  /**
   * Returns the start-of-day value for a date.
   */
  private startOfDay(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }

  /**
   * Calculates day difference between two dates.
   */
  private diffInDays(start: Date, end: Date): number {
    const millisInDay = 24 * 60 * 60 * 1000;
    return Math.round((end.getTime() - start.getTime()) / millisInDay);
  }

  /**
   * Returns the later of two dates.
   */
  private maxDate(left: Date, right: Date): Date {
    return left > right ? left : right;
  }

  /**
   * Returns the earlier of two dates.
   */
  private minDate(left: Date, right: Date): Date {
    return left < right ? left : right;
  }

  /**
   * Checks if a date's month is inside navigation boundaries.
   */
  private isMonthWithinRange(date: Date): boolean {
    const targetMonth = this.startOfMonth(date);
    const minAllowed = this.addMonths(this.baseMonth, -6);
    const maxAllowed = this.addMonths(this.baseMonth, 6);
    return targetMonth >= minAllowed && targetMonth <= maxAllowed;
  }

  /**
   * Returns a date shifted by a number of months.
   */
  private addMonths(date: Date, months: number): Date {
    return new Date(date.getFullYear(), date.getMonth() + months, 1);
  }

  /**
   * Normalizes Firestore or plain date-like values to Date.
   */
  private asDate(value: any): Date {
    if (value?.toDate) {
      return value.toDate();
    }

    return new Date(value);
  }

  /**
   * Returns the calendar color for a specific event.
   */
  getEventCalendarColor(event: CalendarEvent): string {
    return this.getDotColor(this.getCalendarIdFromEvent(event));
  }

  /**
   * Extracts calendar document id from an event parent path.
   */
  private getCalendarIdFromEvent(event: CalendarEvent): string {
    if (!event) {
      return '';
    }

    const parentPath = event.parentPath || '';
    const parts = parentPath.split('/');
    if (parts.length >= 2) {
      return parts[1];
    }

    return '';
  }

  /**
   * Finds a calendar by its document id.
   */
  private findCalendarById(calendarId: string): Calendar | undefined {
    return this.calendarContainers.find(
      (entry) => entry.calendar.id === calendarId
    )?.calendar;
  }

  /**
   * Generates a stable fallback color for a calendar id.
   */
  private getColorByCalendarId(calendarId: string): string {
    const palette = [
      '#1a73e8',
      '#34a853',
      '#fbbc04',
      '#d93025',
      '#9334e6',
      '#00897b',
    ];
    const hash = calendarId
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return palette[Math.abs(hash) % palette.length];
  }
}
